
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import IconChip from '@/core/components/IconChip.vue';
import PaymentChip from '@/core/components/PaymentChip.vue';
import InputSlider from '@/core/components/InputSlider.vue';
import DollarInput from '@/core/components/DollarInput.vue';
import { namespace } from 'vuex-class';
import { LoanDetails } from '@/loan/types/loan-details';
import monthlyIconSVG from '@/assets/images/monthlyIcon.svg';
import LoanProjectionChart from '@/loan/components/LoanProjectionChart.vue';
import { LoanProjection } from '@/loan/types/loan-projection';
import { Debouncer } from '@/core/types/debouncer';
import { LoanProjectionRequestQuery } from '@/loan/types/loan-projection-request-query';
import { LoanProjectionType } from '@/loan/enums/loan-projection-type';
import { LoanProjectionChartContext } from '@/loan/types/loan-projection-chart-context';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');
const LESS_THAN_MINIMUM_PAYMENT_AMOUNT_TEXT = 'New amount should be at or above your minimum monthly payment.';
const GREATER_THAN_PAYOFF_AMOUNT_TEXT = (max: number) => `The maximum monthly payment amount is ${UtilHelper.formatDollar(max)}.`;
const ICON_CHIP_TEXT = 'Find a payment that meets your needs.';
const ICON_CHIP_TITLE = 'Test drive a new monthly payment.';
const TEST_ID = 'Kloans-Loan-LoanMonthly';

@Component({
	components: {
		IconChip,
		PaymentChip,
		InputSlider,
		DollarInput,
		LoanProjectionChart,
	},
})
export default class LoanMonthly extends Vue {
	public TEST_ID = TEST_ID;

	@loanNamespace.Getter('autopayTotalAmount')
	public autopayTotalAmount!: number;

	public formatDollar = UtilHelper.formatDollar;

	@loanNamespace.Action('getAndSetProjection')
	public getAndSetProjection!: (
		{ loanId, query }:
		{ loanId: string; query: LoanProjectionRequestQuery }
	) => Promise<void>;

	public iconChipIcon = monthlyIconSVG;

	public iconChipText = ICON_CHIP_TEXT;

	public iconChipTitle = ICON_CHIP_TITLE;

	@Prop({ required: false })
	public amount!: number;

	@loanNamespace.Getter('isAutopayOn')
	public isAutopayOn!: boolean;

	public isProjectionLoading = false;

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	@loanNamespace.Getter('minimumMonthlyPayment')
	public minimumMonthlyPayment!: number;

	public nextPaymentInputDebouncer!: Debouncer<number>;

	public nextPaymentInputErrorMesasge: string | null = null;

	public nextPaymentInputValue = 0;

	@loanNamespace.State('projection')
	public projection!: LoanProjection;

	public projectionChartContext: LoanProjectionChartContext | null = null;

	@loanNamespace.Getter('startBalance')
	public startBalance!: number;

	get isNextPaymentButtonDisabled(): boolean {
		return (this.nextPaymentInputValue < this.nextPaymentInputMin)
			|| (this.nextPaymentInputValue > this.nextPaymentInputMax);
	}

	get nextPaymentInitialInputValue(): number {
		return this.isAutopayOn
			? this.autopayTotalAmount
			: Number(this.loanDetails.general.installmentAmount);
	}

	get nextPaymentInputMax(): number {
		const maxNum = 3 * Number(this.loanDetails.general.installmentAmount);
		return Math.round(maxNum * 100) / 100;
	}

	get nextPaymentInputMin(): number {
		return Number(this.loanDetails.general.installmentAmount);
	}

	mounted(): void {
		this.projectionChartContext = {
			isOriginal: true,
			projection: this.projection,
		};

		this.nextPaymentInputDebouncer = new Debouncer({
			callback: this.setProjectionContextFromInput,
			debounceTime: 0,
			skipIfSame: true,
		});

		if (!this.amount) {
			this.nextPaymentInputValue = this.nextPaymentInitialInputValue;
		} else {
			this.nextPaymentInputValue = this.amount;
		}
	}

	public async setProjectionContextFromInput(value: number, isOriginal = false): Promise<void> {
		this.isProjectionLoading = true;

		const query: LoanProjectionRequestQuery = {
			property: LoanProjectionType.AUTOPAY,
			value: String(value),
		};

		try {
			await this.getAndSetProjection({
				loanId: this.loanId,
				query,
			});

			this.projectionChartContext = {
				isOriginal,
				projection: this.projection,
			};
		} catch (error) {
			console.error(error);
		}

		this.isProjectionLoading = false;
	}

	public onNextPaymentInputChange(value: number): void {
		if (this.nextPaymentInputValue === value) {
			return;
		}

		this.nextPaymentInputValue = value;
		this.nextPaymentInputErrorMesasge = null;

		if (this.nextPaymentInputValue < this.nextPaymentInputMin) {
			this.nextPaymentInputErrorMesasge = LESS_THAN_MINIMUM_PAYMENT_AMOUNT_TEXT;
			return;
		}

		if (this.nextPaymentInputValue > this.nextPaymentInputMax) {
			this.nextPaymentInputErrorMesasge = GREATER_THAN_PAYOFF_AMOUNT_TEXT(this.nextPaymentInputMax);
			return;
		}

		this.nextPaymentInputDebouncer.debounce(value);
	}
}

